import { useState, useMemo, useEffect } from 'react'
import type { ElementoModulo } from './tipos'
import { useHerramientasDisponibles } from '@/modules/programacion/useHerramientas'
import { fotoDeHerramienta } from '@/lib/supabase/deposito'
import {
  useCatalogoInventarioMateriales,
  type ItemInventarioMaterial,
} from './useBuscarInventario'
import {
  IconoHerramienta,
  IconoCaja,
  IconoLista,
  IconoCheck,
  IconoBasura,
  IconoCerrar,
} from '@/shared/ui/Iconos/Iconos'
import estilos from './ModalInsumosModulo.module.css'

type Props = {
  abierto: boolean
  moduloNombre: string
  moduloCodigo: string
  elementosActuales: ElementoModulo[]
  onGuardar: (elementos: ElementoModulo[]) => void
  onCerrar: () => void
}

export function ModalInsumosModulo({
  abierto,
  moduloNombre,
  moduloCodigo,
  elementosActuales,
  onGuardar,
  onCerrar,
}: Props) {
  const [tab, setTab] = useState<'herramientas' | 'materiales' | 'resumen'>('herramientas')
  const [elementos, setElementos] = useState<ElementoModulo[]>(() => [...elementosActuales])

  // Sincronizar elementos cuando se abre el modal o cambian las propiedades
  useEffect(() => {
    if (abierto) {
      setElementos([...elementosActuales])
    }
  }, [abierto, elementosActuales])

  // Catálogo real de herramientas de almacén
  const { herramientas = [], cargando: cargandoHerr } = useHerramientasDisponibles()
  const [busquedaHerramientas, setBusquedaHerramientas] = useState('')

  // Catálogo buscable de inventario real (vista_inventario)
  const [busquedaMaterial, setBusquedaMaterial] = useState('')
  const [soloConStock, setSoloConStock] = useState(false)
  const { items: catalogoMateriales, cargando: cargandoMateriales } = useCatalogoInventarioMateriales(
    busquedaMaterial,
    soloConStock
  )

  // Cantidad y unidad seleccionada por cada fila de la tabla de inventario
  const [cantidadesFila, setCantidadesFila] = useState<Record<string, number>>({})
  const [unidadesFila, setUnidadesFila] = useState<Record<string, string>>({})

  // Formulario ítem manual / no catalogado
  const [mostrarManual, setMostrarManual] = useState(false)
  const [manualDesc, setManualDesc] = useState('')
  const [manualCodigo, setManualCodigo] = useState('')
  const [manualCant, setManualCant] = useState(1)
  const [manualUnidad, setManualUnidad] = useState('UND')

  // Mapa de herramientas seleccionadas actualmente en `elementos` { [codigo/id]: cantidad }
  const herramientasSeleccionadas = useMemo(() => {
    const mapa: Record<string, number> = {}
    elementos
      .filter((e) => e.tipo === 'herramienta')
      .forEach((e) => {
        const key = e.codigo || e.id
        mapa[key] = (mapa[key] || 0) + e.cantidad
      })
    return mapa
  }, [elementos])

  // Mapa de materiales asignados { [código o descripción]: { cantidad, unidad } }
  const materialesSeleccionados = useMemo(() => {
    const mapa: Record<string, { cantidad: number; unidad: string }> = {}
    elementos
      .filter((e) => e.tipo === 'material' || !e.tipo)
      .forEach((e) => {
        const keyCod = e.codigo ? e.codigo.toLowerCase().trim() : null
        const keyDesc = e.descripcion.toLowerCase().trim()
        if (keyCod) {
          mapa[keyCod] = {
            cantidad: (mapa[keyCod]?.cantidad || 0) + Number(e.cantidad || 0),
            unidad: e.unidad || 'UND',
          }
        }
        mapa[keyDesc] = {
          cantidad: (mapa[keyDesc]?.cantidad || 0) + Number(e.cantidad || 0),
          unidad: e.unidad || 'UND',
        }
      })
    return mapa
  }, [elementos])

  const herramientasFiltradas = useMemo(() => {
    const q = busquedaHerramientas.toLowerCase().trim()
    if (!q) return herramientas
    return herramientas.filter(
      (h) =>
        h.nombre.toLowerCase().includes(q) ||
        (h.codigo && h.codigo.toLowerCase().includes(q))
    )
  }, [herramientas, busquedaHerramientas])

  if (!abierto) return null

  // Modificar herramientas seleccionadas
  function handleCambiarHerramienta(h: any, delta: number) {
    const key = h.codigo || h.id
    const actual = herramientasSeleccionadas[key] || 0
    const nuevaCant = Math.max(0, actual + delta)

    let actualizados = elementos.filter(
      (e) => !(e.tipo === 'herramienta' && (e.codigo === key || e.id === key))
    )

    if (nuevaCant > 0) {
      actualizados.push({
        id: crypto.randomUUID(),
        tipo: 'herramienta',
        codigo: h.codigo || undefined,
        descripcion: h.nombre,
        unidad: 'UND',
        cantidad: nuevaCant,
      })
    }
    setElementos(actualizados)
  }

  // Agregar material desde la tabla interactiva de inventario
  function handleAgregarDesdeInventario(item: ItemInventarioMaterial) {
    const key = String(item.id)
    const cant = Number(cantidadesFila[key]) || 1
    const und = (unidadesFila[key] || item.unidad || 'UND').trim().toUpperCase()

    const itemDesc = item.nombre || item.descripcion
    const existenteIdx = elementos.findIndex(
      (e) =>
        (e.tipo === 'material' || !e.tipo) &&
        ((item.codigo && e.codigo && e.codigo === item.codigo) ||
          e.descripcion.toLowerCase() === itemDesc.toLowerCase())
    )

    if (existenteIdx >= 0) {
      const copia = [...elementos]
      copia[existenteIdx] = {
        ...copia[existenteIdx],
        id_inventario: copia[existenteIdx].id_inventario || item.id,
        cantidad: Number((copia[existenteIdx].cantidad + cant).toFixed(2)),
      }
      setElementos(copia)
    } else {
      const nuevo: ElementoModulo = {
        id: crypto.randomUUID(),
        tipo: 'material',
        codigo: item.codigo || undefined,
        id_inventario: item.id,
        descripcion: itemDesc,
        unidad: und,
        cantidad: cant,
      }
      setElementos([...elementos, nuevo])
    }
  }

  // Agregar material manual
  function handleAgregarMaterialManual(e: React.FormEvent) {
    e.preventDefault()
    if (!manualDesc.trim()) return

    const nuevo: ElementoModulo = {
      id: crypto.randomUUID(),
      tipo: 'material',
      codigo: manualCodigo.trim() || undefined,
      descripcion: manualDesc.trim(),
      unidad: (manualUnidad.trim() || 'UND').toUpperCase(),
      cantidad: Number(manualCant) || 1,
    }

    setElementos([...elementos, nuevo])
    setManualDesc('')
    setManualCodigo('')
    setManualCant(1)
    setManualUnidad('UND')
    setMostrarManual(false)
  }

  function handleQuitarElemento(id: string) {
    setElementos((prev) => prev.filter((e) => e.id !== id))
  }

  function handleCambiarCantidadElemento(id: string, delta: number) {
    setElementos((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e
        const nuevaCant = Math.max(0.1, Number((e.cantidad + delta).toFixed(2)))
        return { ...e, cantidad: nuevaCant }
      })
    )
  }

  const totalHerramientas = elementos.filter((e) => e.tipo === 'herramienta').length
  const materialesAsignados = elementos.filter((e) => e.tipo === 'material' || !e.tipo)
  const totalMateriales = materialesAsignados.length

  return (
    <div className={estilos.overlay} onClick={onCerrar}>
      <div className={estilos.modal} onClick={(e) => e.stopPropagation()}>
        <header className={estilos.cabecera}>
          <div>
            <h2 className={estilos.tituloPrincipal}>
              <IconoHerramienta />
              <span>Insumos y Herramientas del Módulo</span>
            </h2>
            <p className={estilos.subtituloModulo}>
              {moduloCodigo} — {moduloNombre}
            </p>
          </div>
          <button type="button" className={estilos.btnCerrar} onClick={onCerrar} title="Cerrar modal">
            <IconoCerrar />
          </button>
        </header>

        {/* Pestañas de navegación */}
        <nav className={estilos.navTabs}>
          <button
            type="button"
            className={`${estilos.tab} ${tab === 'herramientas' ? estilos.tabActivo : ''}`}
            onClick={() => setTab('herramientas')}
          >
            <IconoHerramienta />
            <span>Herramientas y Equipos</span>
            <span className={estilos.badgeCuenta}>{totalHerramientas}</span>
          </button>
          <button
            type="button"
            className={`${estilos.tab} ${tab === 'materiales' ? estilos.tabActivo : ''}`}
            onClick={() => setTab('materiales')}
          >
            <IconoCaja />
            <span>Materiales e Insumos</span>
            <span className={estilos.badgeCuenta}>{totalMateriales}</span>
          </button>
          <button
            type="button"
            className={`${estilos.tab} ${tab === 'resumen' ? estilos.tabActivo : ''}`}
            onClick={() => setTab('resumen')}
          >
            <IconoLista />
            <span>Lista Completa ({elementos.length})</span>
          </button>
        </nav>

        {/* Contenido según pestaña */}
        <div className={estilos.cuerpo}>
          {tab === 'herramientas' && (
            <>
              <input
                type="text"
                placeholder="Buscar herramienta por nombre o código..."
                value={busquedaHerramientas}
                onChange={(e) => setBusquedaHerramientas(e.target.value)}
                className={estilos.barraBuscar}
              />

              {cargandoHerr && <p style={{ color: '#cfbdf7' }}>Cargando catálogo...</p>}

              <div className={estilos.grillaHerramientas}>
                {herramientasFiltradas.map((h) => {
                  const key = h.codigo || h.id
                  const cant = herramientasSeleccionadas[key] || 0
                  const seleccionada = cant > 0
                  const urlFoto = fotoDeHerramienta(h.foto)

                  return (
                    <div
                      key={h.id}
                      className={`${estilos.tarjetaHerramienta} ${
                        seleccionada ? estilos.tarjetaHerramientaSeleccionada : ''
                      }`}
                    >
                      {urlFoto ? (
                        <img src={urlFoto} alt={h.nombre} className={estilos.herramientaFoto} />
                      ) : (
                        <div className={estilos.herramientaSinFoto}>
                          <IconoHerramienta />
                        </div>
                      )}

                      <div className={estilos.herramientaInfo}>
                        <span className={estilos.herramientaNombre}>{h.nombre}</span>
                        {h.codigo && (
                          <span className={estilos.herramientaCodigo}>Cód: {h.codigo}</span>
                        )}
                      </div>

                      <div className={estilos.herramientaControles}>
                        {seleccionada ? (
                          <div className={estilos.controlCantidad}>
                            <button
                              type="button"
                              className={estilos.btnCantidad}
                              onClick={() => handleCambiarHerramienta(h, -1)}
                            >
                              -
                            </button>
                            <span className={estilos.cantidadNum}>{cant}</span>
                            <button
                              type="button"
                              className={estilos.btnCantidad}
                              onClick={() => handleCambiarHerramienta(h, 1)}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className={estilos.btnSeleccionarHerramienta}
                            onClick={() => handleCambiarHerramienta(h, 1)}
                          >
                            + Seleccionar
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {tab === 'materiales' && (
            <div className={estilos.contenedorMateriales}>
              {/* Barra de Búsqueda y Filtros de Inventario */}
              <div className={estilos.barraFiltrosInventario}>
                <input
                  type="text"
                  placeholder="Buscar material en inventario por descripción o código..."
                  value={busquedaMaterial}
                  onChange={(e) => setBusquedaMaterial(e.target.value)}
                  className={estilos.barraBuscar}
                  style={{ flex: 1, minWidth: '220px' }}
                />

                <label className={estilos.filtroStockCheck}>
                  <input
                    type="checkbox"
                    checked={soloConStock}
                    onChange={(e) => setSoloConStock(e.target.checked)}
                  />
                  <span>Solo con stock</span>
                </label>

                <button
                  type="button"
                  className={estilos.btnToggleManual}
                  onClick={() => setMostrarManual(!mostrarManual)}
                >
                  {mostrarManual ? 'Cerrar manual' : '+ Ítem manual / no catalogado'}
                </button>
              </div>

              {/* Formulario colapsable para ítem manual no catalogado */}
              {mostrarManual && (
                <form onSubmit={handleAgregarMaterialManual} className={estilos.formManualColapsable}>
                  <strong style={{ fontSize: '12px', color: '#ffc43f' }}>
                    Añadir material personalizado (fuera de inventario):
                  </strong>
                  <div className={estilos.formFila}>
                    <div style={{ flex: 2 }}>
                      <input
                        type="text"
                        placeholder="Descripción del material..."
                        value={manualDesc}
                        onChange={(e) => setManualDesc(e.target.value)}
                        className={estilos.barraBuscar}
                        required
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Cód / Ref (opc)"
                        value={manualCodigo}
                        onChange={(e) => setManualCodigo(e.target.value)}
                        className={estilos.barraBuscar}
                      />
                    </div>
                    <div style={{ width: '80px' }}>
                      <input
                        type="number"
                        min="0.1"
                        step="any"
                        placeholder="Cant"
                        value={manualCant}
                        onChange={(e) => setManualCant(Number(e.target.value))}
                        className={estilos.barraBuscar}
                        required
                      />
                    </div>
                    <div style={{ width: '85px' }}>
                      <select
                        value={manualUnidad}
                        onChange={(e) => setManualUnidad(e.target.value)}
                        className={estilos.selectUnidadTabla}
                        style={{ height: '36px', width: '100%' }}
                        title="Unidad de medida"
                      >
                        <option value="UND">UND</option>
                        <option value="KG">KG</option>
                        <option value="M">M</option>
                        <option value="GL">GL</option>
                        <option value="PAQ">PAQ</option>
                        <option value="KIT">KIT</option>
                      </select>
                    </div>
                    <button type="submit" className={estilos.btnAgregarMaterial}>
                      <span>+ Añadir</span>
                    </button>
                  </div>
                </form>
              )}

              {/* Tabla Interactiva de Materiales desde vista_inventario */}
              <div className={estilos.contenedorTablaInventario}>
                {cargandoMateriales ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#cfbdf7' }}>
                    Consultando inventario en tiempo real...
                  </div>
                ) : catalogoMateriales.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#cfbdf7' }}>
                    No se encontraron productos en inventario con ese criterio.
                  </div>
                ) : (
                  <table className={estilos.tablaInventario}>
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Código</th>
                        <th>Descripción del Producto</th>
                        <th style={{ width: '130px' }}>Bodega</th>
                        <th style={{ width: '75px' }}>Cantidad</th>
                        <th style={{ width: '85px' }}>Unidad</th>
                        <th style={{ width: '110px' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogoMateriales.map((item) => {
                        const rowKey = String(item.id)
                        const itemDesc = item.nombre || item.descripcion
                        const keyCod = item.codigo ? item.codigo.toLowerCase().trim() : null
                        const keyDesc = itemDesc.toLowerCase().trim()
                        const asignadoActual = (keyCod && materialesSeleccionados[keyCod]) || materialesSeleccionados[keyDesc]
                        const estaAsignado = Boolean(asignadoActual && asignadoActual.cantidad > 0)

                        const cant = cantidadesFila[rowKey] ?? 1
                        const und = unidadesFila[rowKey] ?? item.unidad ?? 'UND'

                        let stockBadge = (
                          <span className={estilos.stockBajoDemanda}>Bajo demanda</span>
                        )
                        if (item.se_inventaria) {
                          if (item.existencias != null && item.existencias > 0) {
                            stockBadge = (
                              <span className={estilos.stockDisponible}>
                                {item.existencias} en bodega
                              </span>
                            )
                          } else {
                            stockBadge = (
                              <span className={estilos.stockAgotado}>Agotado (0)</span>
                            )
                          }
                        }

                        return (
                          <tr
                            key={item.id}
                            className={estaAsignado ? estilos.filaMaterialSeleccionada : undefined}
                          >
                            <td>
                              {item.codigo ? (
                                <span className={estilos.codigoMat}>{item.codigo}</span>
                              ) : (
                                <span style={{ color: '#6330b3' }}>—</span>
                              )}
                            </td>
                            <td>
                              <span className={estilos.descMat}>{itemDesc}</span>
                              {estaAsignado && (
                                <div>
                                  <span className={estilos.badgeAsignado}>
                                    <IconoCheck /> {asignadoActual.cantidad} {asignadoActual.unidad} en módulo
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>{stockBadge}</td>
                            <td>
                              <input
                                type="number"
                                min="0.1"
                                step="any"
                                value={cant}
                                onChange={(e) =>
                                  setCantidadesFila({
                                    ...cantidadesFila,
                                    [rowKey]: Number(e.target.value),
                                  })
                                }
                                className={estilos.inputCantidadTabla}
                              />
                            </td>
                            <td>
                              <select
                                value={und}
                                onChange={(e) =>
                                  setUnidadesFila({
                                    ...unidadesFila,
                                    [rowKey]: e.target.value,
                                  })
                                }
                                className={estilos.selectUnidadTabla}
                              >
                                <option value="UND">UND</option>
                                <option value="KG">KG</option>
                                <option value="M">M</option>
                                <option value="GL">GL</option>
                                <option value="PAQ">PAQ</option>
                                <option value="KIT">KIT</option>
                              </select>
                            </td>
                            <td>
                              <button
                                type="button"
                                className={`${estilos.btnAnadirTabla} ${estaAsignado ? estilos.btnAnadirMas : ''}`}
                                onClick={() => handleAgregarDesdeInventario(item)}
                                title={estaAsignado ? 'Añadir más cantidad a este módulo' : 'Añadir insumo al módulo'}
                              >
                                {estaAsignado ? '+ Agregar más' : '+ Añadir'}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Sección de Materiales Asignados al Módulo */}
              <div className={estilos.seccionMaterialesAsignados}>
                <div className={estilos.tituloSeccion}>
                  <span>Materiales requeridos para este módulo ({materialesAsignados.length})</span>
                </div>

                {materialesAsignados.length === 0 ? (
                  <p style={{ color: '#cfbdf7', fontSize: '12px', margin: '4px 0' }}>
                    No hay materiales asignados a este módulo todavía. Usa la tabla superior o añade un ítem manual.
                  </p>
                ) : (
                  <table className={estilos.tablaElementos}>
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Código</th>
                        <th>Descripción</th>
                        <th style={{ width: '110px' }}>Cantidad</th>
                        <th style={{ width: '80px' }}>Unidad</th>
                        <th style={{ width: '50px' }}>Quitar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialesAsignados.map((mat) => (
                        <tr key={mat.id}>
                          <td>
                            {mat.codigo ? (
                              <span className={estilos.codigoMat}>{mat.codigo}</span>
                            ) : (
                              <span style={{ color: '#6330b3' }}>—</span>
                            )}
                          </td>
                          <td>
                            <strong>{mat.descripcion}</strong>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <button
                                type="button"
                                className={estilos.btnCantidad}
                                onClick={() => handleCambiarCantidadElemento(mat.id, -1)}
                              >
                                -
                              </button>
                              <span style={{ color: '#ffc43f', fontWeight: 800, minWidth: '22px', textAlign: 'center' }}>
                                {mat.cantidad}
                              </span>
                              <button
                                type="button"
                                className={estilos.btnCantidad}
                                onClick={() => handleCambiarCantidadElemento(mat.id, 1)}
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td style={{ color: '#cfbdf7' }}>{mat.unidad}</td>
                          <td>
                            <button
                              type="button"
                              className={estilos.btnQuitarElemento}
                              onClick={() => handleQuitarElemento(mat.id)}
                              title="Eliminar material"
                            >
                              <IconoBasura />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {tab === 'resumen' && (
            <div>
              {elementos.length === 0 ? (
                <p style={{ color: '#cfbdf7', textAlign: 'center', padding: '20px' }}>
                  No hay materiales ni herramientas agregadas para este módulo aún.
                </p>
              ) : (
                <table className={estilos.tablaElementos}>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Descripción</th>
                      <th>Cantidad</th>
                      <th>Unidad</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elementos.map((elem) => (
                      <tr key={elem.id}>
                        <td>
                          <span
                            className={`${estilos.chipTipo} ${
                              elem.tipo === 'herramienta'
                                ? estilos.chipTipoHerramienta
                                : estilos.chipTipoMaterial
                            }`}
                          >
                            {elem.tipo === 'herramienta' ? 'Herramienta' : 'Material'}
                          </span>
                        </td>
                        <td>
                          <strong>{elem.descripcion}</strong>
                          {elem.codigo && (
                            <div style={{ fontSize: '10.5px', color: '#cfbdf7' }}>
                              Cód: {elem.codigo}
                            </div>
                          )}
                        </td>
                        <td style={{ color: '#ffc43f', fontWeight: 800 }}>{elem.cantidad}</td>
                        <td style={{ color: '#cfbdf7' }}>{elem.unidad}</td>
                        <td>
                          <button
                            type="button"
                            className={estilos.btnQuitarElemento}
                            onClick={() => handleQuitarElemento(elem.id)}
                            title="Quitar"
                          >
                            <IconoBasura />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Pie con totales y botón guardar */}
        <footer className={estilos.pie}>
          <div className={estilos.resumenPie}>
            Total: <b>{totalMateriales}</b> materiales · <b>{totalHerramientas}</b> herramientas
          </div>
          <div className={estilos.accionesPie}>
            <button type="button" className={estilos.btnCancelar} onClick={onCerrar}>
              Cancelar
            </button>
            <button
              type="button"
              className={estilos.btnGuardar}
              onClick={() => {
                onGuardar(elementos)
                onCerrar()
              }}
            >
              <IconoCheck /> Guardar en Módulo
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
